import { Link } from "react-router-dom";
import { FaRegCopy } from "react-icons/fa6";
import { X } from "lucide-react";

import { toastNotification } from "@/utils/toastNotification";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
} from "@/components/ui/card";
import OrderStatusButton from "./OrderStatusButton";
import type { UsersOrderData } from "@/types";

interface CurrentOrderPopoutProps {
	currentOrder: UsersOrderData;
	setCurrentOrder: React.Dispatch<
		React.SetStateAction<UsersOrderData | undefined>
	>;
}

const CurrentOrderPopout = ({
	currentOrder,
	setCurrentOrder,
}: CurrentOrderPopoutProps) => {
	const deliveryChargeMapper = (deliveryChargeId: number) => {
		if (deliveryChargeId === 1) {
			return "Free";
		}

		return "£3.00";
	};

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center">
			<div
				className="fixed inset-0 bg-black/50 backdrop-blur-sm"
				onClick={() => setCurrentOrder(undefined)}
				onKeyDown={(e) => e.key === "Escape" && setCurrentOrder(undefined)}
				role="button"
				tabIndex={0}
			/>
			<Card
				className="relative z-[101] w-full max-w-2xl overflow-hidden bg-white shadow-lg"
				x-chunk="dashboard-05-chunk-4"
			>
				<CardHeader className="flex flex-row items-start justify-between bg-muted/50">
					<div className="grid gap-0.5">
						<CardTitle className="group flex items-center gap-2">
							<p>Order {currentOrder?.id}</p>
							<Button
								size="icon"
								variant="outline"
								className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 text-lg"
								onClick={() => {
									navigator.clipboard.writeText(currentOrder.id.toString());
									toastNotification(
										"Copied order ID to clipboard",
										"bg-success text-white",
									);
								}}
							>
								<FaRegCopy className="h-3 w-3" />
								<span className="sr-only">Copy Order ID</span>
							</Button>
						</CardTitle>
						<CardDescription>
							<div className="flex flex-col gap-2">
								<p>
									Created:{" "}
									{new Date(currentOrder.createdAt).toLocaleDateString("en-GB")}
								</p>
								<p>
									Collection Date:{" "}
									{new Date(currentOrder.collectionDate).toLocaleDateString(
										"en-GB",
									)}
								</p>
							</div>
						</CardDescription>
					</div>
					<div className="flex flex-col items-end gap-2">
						<Button
							variant="ghost"
							size="icon"
							onClick={() => setCurrentOrder(undefined)}
							className="h-8 w-8"
						>
							<X className="h-4 w-4" />
							<span className="sr-only">Close</span>
						</Button>
						<OrderStatusButton
							currentOrder={currentOrder}
							setCurrentOrder={setCurrentOrder}
						/>
					</div>
				</CardHeader>
				<CardContent className="p-6 text-sm">
					<div className="grid gap-6">
						<div>
							<div className="font-semibold mb-3">Order Details</div>
							<div className="space-y-3">
								{currentOrder.basketItems.map((item) => (
									<div
										key={item.id}
										className="flex items-center justify-between"
									>
										<div className="flex items-center gap-2">
											<span className="font-medium">{item.title}</span>
											<span className="text-muted-foreground">
												x {item.quantity}
											</span>
										</div>
										<span className="font-medium">
											£{(item.price * item.quantity).toFixed(2)}
										</span>
									</div>
								))}
							</div>
							<Separator className="my-4" />
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<span className="text-muted-foreground">Delivery Charge</span>
									<span>
										{deliveryChargeMapper(currentOrder.deliveryMethod.id)}
									</span>
								</div>
								<div className="flex items-center justify-between font-semibold">
									<span>Total</span>
									<span>£{currentOrder.totalPrice.toFixed(2)}</span>
								</div>
							</div>
						</div>

						<Separator />

						<div>
							<div className="font-semibold mb-3">Customer Information</div>
							<div className="grid gap-4">
								<div>
									<div className="font-medium mb-1">Address</div>
									<address className="grid gap-0.5 not-italic text-muted-foreground">
										<span>{currentOrder.user.fullName}</span>
										<span>
											{currentOrder.user.houseNo} {currentOrder.user.street}
										</span>
										<span>{currentOrder.user.postcode?.toUpperCase()}</span>
									</address>
								</div>
								<div className="grid gap-2">
									<div className="flex items-center justify-between">
										<span className="text-muted-foreground">Email</span>
										<Link
											to={`mailto:${currentOrder.user.email}`}
											className="hover:underline"
										>
											{currentOrder.user.email}
										</Link>
									</div>
									<div className="flex items-center justify-between">
										<span className="text-muted-foreground">Phone</span>
										{currentOrder.user.phoneNumber ? (
											<Link
												to={`tel:${currentOrder.user.phoneNumber}`}
												className="hover:underline"
											>
												{currentOrder.user.phoneNumber}
											</Link>
										) : (
											<span className="text-muted-foreground">
												No number provided
											</span>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</CardContent>

				{currentOrder.notes && (
					<CardFooter className="flex flex-row items-center border-t bg-muted/50 px-6 py-3">
						<div className="text-sm">
							<span className="font-medium">Customer Notes:</span>{" "}
							<span className="text-muted-foreground">
								{currentOrder.notes}
							</span>
						</div>
					</CardFooter>
				)}
			</Card>
		</div>
	);
};

export default CurrentOrderPopout;
